import { useState } from "react";
import { CAMPAIGN_PHASES } from "../game/campaign";
import { KATAS } from "../game/katas";
import { unlockRegisteredTracks } from "../audio/useTrack";
import {
  useGameStore,
  HeightMode,
  SingleHand,
  WieldMode,
} from "../game/useGameStore";
import {
  colors,
  disabledButton,
  labels,
  overlay,
  primaryButton,
  secondaryButton,
  typography,
} from "./theme";

type MenuView = "main" | "campaign" | "exit-confirm";

interface MenuProps {
  onEnterVR: () => void;
  menuThemePaused: boolean;
  onToggleMenuTheme: () => void;
  onExit: () => void;
}

export function Menu({
  onEnterVR,
  menuThemePaused,
  onToggleMenuTheme,
  onExit,
}: MenuProps) {
  const [view, setView] = useState<MenuView>("main");
  const wield = useGameStore((s) => s.settings.wield);
  const singleHand = useGameStore((s) => s.settings.singleHand);
  const heightMode = useGameStore((s) => s.settings.height);
  const setHeightMode = useGameStore((s) => s.setHeightMode);
  const cutArrowInverted = useGameStore((s) => s.settings.cutArrowInverted);
  const setCutArrowInverted = useGameStore((s) => s.setCutArrowInverted);
  const telemetryOptOut = useGameStore((s) => s.telemetryOptOut);
  const setTelemetryOptOut = useGameStore((s) => s.setTelemetryOptOut);
  const selectedPhaseIndex = useGameStore((s) => s.selectedPhaseIndex);
  const unlockedPhaseIndex = useGameStore((s) => s.unlockedPhaseIndex);
  const phaseBest = useGameStore((s) => s.phaseBest);
  const setWield = useGameStore((s) => s.setWield);
  const setSingleHand = useGameStore((s) => s.setSingleHand);
  const selectPhase = useGameStore((s) => s.selectPhase);
  const startCampaignPhase = useGameStore((s) => s.startCampaignPhase);
  const reviewCampaignTutorial = useGameStore((s) => s.reviewCampaignTutorial);
  const openSurvivalIntro = useGameStore((s) => s.openSurvivalIntro);
  const survivalBest = useGameStore((s) => s.survivalBest);
  const setPhase = useGameStore((s) => s.setPhase);
  const unlockedKatas = useGameStore((s) => s.unlockedKatas);

  const selectedPhase = CAMPAIGN_PHASES[selectedPhaseIndex];

  const handleCampaignStart = () => {
    unlockRegisteredTracks();
    startCampaignPhase(selectedPhaseIndex);
    if (useGameStore.getState().phase !== "teach") {
      onEnterVR();
    }
  };

  const handleReviewLesson = () => {
    unlockRegisteredTracks();
    reviewCampaignTutorial(selectedPhaseIndex);
  };

  const handleReviewOnboarding = () => {
    unlockRegisteredTracks();
    reviewCampaignTutorial(0, 0);
  };

  const handleSurvivalStart = () => {
    openSurvivalIntro();
  };

  return (
    <div style={menuOverlay}>
      <div style={titleBlock}>
        <h1 style={title}>SAMURAI QUEST</h1>
        <h2 style={subtitle}>侍クエスト</h2>
      </div>

      {view === "main" ? (
        <nav style={mainActions}>
          <button onClick={() => setView("campaign")} style={mainPrimaryBtn}>
            Campaign
          </button>
          <button onClick={handleSurvivalStart} style={mainPrimaryBtn}>
            Survival
            {survivalBest > 0 && (
              <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.72 }}>
                Best {survivalBest.toLocaleString("en-US")}
              </span>
            )}
          </button>
          <button onClick={() => setView("exit-confirm")} style={exitBtn}>
            Exit
          </button>
        </nav>
      ) : view === "campaign" ? (
        <section style={campaignPanel}>
          <div style={campaignHeader}>
            <button onClick={() => setView("main")} style={backBtn}>
              Back
            </button>
            <div style={sectionLabel}>Campaign</div>
          </div>

          <div style={phasePath}>
            {CAMPAIGN_PHASES.map((phase, index) => {
              const locked = index > unlockedPhaseIndex;
              const selected = index === selectedPhaseIndex;
              const cleared = index < unlockedPhaseIndex;
              return (
                <button
                  key={phase.id}
                  disabled={locked}
                  onClick={() => selectPhase(index)}
                  style={{
                    ...phaseNode,
                    borderColor: selected
                      ? colors.amber
                      : cleared
                        ? colors.steel
                        : colors.border,
                    color: locked
                      ? colors.border
                      : selected
                        ? colors.amber
                        : colors.washi,
                    opacity: locked ? 0.42 : 1,
                    background: selected
                      ? "rgba(224, 169, 80, 0.14)"
                      : "rgba(42, 31, 23, 0.58)",
                  }}
                >
                  <span style={phaseNumeral}>{phase.numeral}</span>
                  <span style={phaseName}>{phase.name}</span>
                  <span style={phaseMeta}>
                    {locked
                      ? "Locked"
                      : cleared
                        ? `Best ${phaseBest[index] ?? 0}`
                        : "Current"}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={phaseDetail}>
            <div>
              <div style={phaseDetailName}>
                {selectedPhase.numeral} · {selectedPhase.nameJP}{" "}
                {selectedPhase.name}
              </div>
              <div style={phaseDetailSub}>{selectedPhase.subtitle}</div>
            </div>
            <div style={thresholdText}>
              Threshold {selectedPhase.threshold.toLocaleString("en-US")}
            </div>
            <button onClick={handleCampaignStart} style={campaignBtn}>
              {labels.training} Start
            </button>
            <div style={reviewActions}>
              {selectedPhaseIndex === 0 && (
                <button onClick={handleReviewOnboarding} style={reviewBtn}>
                  Review Onboarding
                </button>
              )}
              <button
                onClick={handleReviewLesson}
                style={{
                  ...reviewBtn,
                  ...(selectedPhaseIndex === 0 ? {} : fullWidthReviewBtn),
                }}
              >
                Review Lesson
              </button>
            </div>
          </div>
        </section>
      ) : (
        <section style={exitConfirmPanel} aria-labelledby="exit-title">
          <div id="exit-title" style={exitConfirmTitle}>
            Leave Samurai Quest?
          </div>
          <p style={exitConfirmCopy}>
            Your campaign progress and records are already saved.
          </p>
          <div style={exitConfirmActions}>
            <button onClick={() => setView("main")} style={exitCancelBtn}>
              Cancel
            </button>
            <button onClick={onExit} style={exitConfirmBtn}>
              Exit
            </button>
          </div>
        </section>
      )}

      <div style={wieldPanel}>
        <div style={sectionLabel}>WIELD</div>
        <div style={{ display: "flex", gap: 14 }}>
          {(["single", "dual"] as WieldMode[]).map((mode) => {
            const selected = wield === mode;
            return (
              <button
                key={mode}
                onClick={() => setWield(mode)}
                style={{
                  ...wieldBtn,
                  borderColor: selected ? colors.amber : colors.border,
                  background: selected
                    ? "rgba(224, 169, 80, 0.14)"
                    : "rgba(42, 31, 23, 0.45)",
                  color: selected ? colors.amber : colors.steel,
                }}
              >
                {mode === "single" ? "Single" : "Dual"}
              </button>
            );
          })}
        </div>
        <p style={hint}>
          {wield === "single" ? `Single hand (${singleHand})` : "Both hands"}
        </p>

        {wield === "single" && (
          <>
            <div style={{ ...sectionLabel, marginTop: 4 }}>HAND</div>
            <div style={{ display: "flex", gap: 14 }}>
              {(["left", "right"] as SingleHand[]).map((hand) => {
                const selected = singleHand === hand;
                return (
                  <button
                    key={hand}
                    onClick={() => setSingleHand(hand)}
                    style={{
                      ...wieldBtn,
                      borderColor: selected ? colors.amber : colors.border,
                      background: selected
                        ? "rgba(224, 169, 80, 0.14)"
                        : "rgba(42, 31, 23, 0.45)",
                      color: selected ? colors.amber : colors.steel,
                    }}
                  >
                    {hand === "left" ? "Left" : "Right"}
                  </button>
                );
              })}
            </div>
          </>
        )}

        <div style={{ ...sectionLabel, marginTop: 12 }}>HEIGHT</div>
        <div style={{ display: "flex", gap: 14 }}>
          {(["standing", "seated"] as HeightMode[]).map((h) => {
            const selected = heightMode === h;
            return (
              <button
                key={h}
                onClick={() => setHeightMode(h)}
                style={{
                  ...wieldBtn,
                  borderColor: selected ? colors.amber : colors.border,
                  background: selected
                    ? "rgba(224, 169, 80, 0.14)"
                    : "rgba(42, 31, 23, 0.45)",
                  color: selected ? colors.amber : colors.steel,
                }}
              >
                {h === "standing" ? "Standing" : "Seated"}
              </button>
            );
          })}
        </div>
        <p style={hint}>
          {heightMode === "standing"
            ? "Play-space floor height"
            : "Raises the play space for a seated headset"}
        </p>

        <div style={{ ...sectionLabel, marginTop: 12 }}>CUT ARROW</div>
        <div style={{ display: "flex", gap: 14 }}>
          {([false, true] as const).map((inverted) => {
            const selected = cutArrowInverted === inverted;
            return (
              <button
                key={String(inverted)}
                onClick={() => setCutArrowInverted(inverted)}
                style={{
                  ...wieldBtn,
                  borderColor: selected ? colors.amber : colors.border,
                  background: selected
                    ? "rgba(224, 169, 80, 0.14)"
                    : "rgba(42, 31, 23, 0.45)",
                  color: selected ? colors.amber : colors.steel,
                }}
              >
                {inverted ? "Against" : "Follow"}
              </button>
            );
          })}
        </div>
        <p style={hint}>
          {cutArrowInverted
            ? "Cut against the arrow (v1)"
            : "Cut the way the arrow points"}
        </p>
      </div>

      <button
        onClick={onToggleMenuTheme}
        title={menuThemePaused ? "Resume menu music" : "Pause menu music"}
        style={musicBtn}
      >
        {menuThemePaused ? ">" : "| |"}
      </button>

      {/* One positioning anchor for this whole cluster — flexbox handles
          each button's actual width/height, so nothing here is a guessed
          vh/px offset (three guesses in a row landed on top of the title,
          then each other, then EXIT — this is the fix that stops guessing). */}
      <div style={bottomRightRow}>
        <div style={dataPanel}>
          <button
            onClick={() => setTelemetryOptOut(!telemetryOptOut)}
            style={{
              ...secondaryButton,
              padding: "12px 18px",
              fontSize: 13,
              borderColor: telemetryOptOut ? colors.border : colors.amber,
              color: telemetryOptOut ? colors.steel : colors.amber,
            }}
          >
            Data: {telemetryOptOut ? "Off" : "On"}
          </button>
          <p style={dataHint}>
            {telemetryOptOut
              ? "Anonymous data not shared"
              : "Anonymous — helps us improve the game"}
          </p>
        </div>

        <button
          onClick={() => setPhase("kata")}
          disabled={unlockedKatas.length === 0}
          title={
            unlockedKatas.length === 0
              ? "Complete a rhythm sequence to unlock"
              : ""
          }
          style={unlockedKatas.length === 0 ? kataLockedBtn : kataBtn}
        >
          {labels.dojo} Katas
          {unlockedKatas.length > 0 && (
            <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.72 }}>
              {unlockedKatas.length}/{KATAS.length}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

const menuOverlay: React.CSSProperties = {
  ...overlay,
  display: "grid",
  gridTemplateColumns: "minmax(280px, 0.8fr) minmax(420px, 600px)",
  alignItems: "center",
  gap: 56,
  padding: "8vh 9vw",
  boxSizing: "border-box",
};

const titleBlock: React.CSSProperties = {
  alignSelf: "start",
  marginTop: "12vh",
  minWidth: 0,
};

const title: React.CSSProperties = {
  ...typography.ui,
  fontFamily:
    "'Zen Kaku Gothic New', 'Segoe UI', 'Yu Gothic', system-ui, sans-serif",
  fontSize: "clamp(30px, 4.4vw, 50px)",
  lineHeight: 1.1,
  fontStyle: "normal",
  fontWeight: 900,
  letterSpacing: "0.16em",
  margin: 0,
  color: colors.amber,
  textShadow: "0 0 28px rgba(224, 169, 80, 0.34)",
};

const subtitle: React.CSSProperties = {
  ...typography.ui,
  fontFamily: "'Zen Old Mincho', 'Times New Roman', 'Yu Mincho', serif",
  fontSize: 30,
  margin: "18px 0 0",
  fontWeight: 900,
  fontStyle: "normal",
  color: colors.steel,
  letterSpacing: "0.12em",
};

const mainActions: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  justifySelf: "stretch",
};

const mainPrimaryBtn: React.CSSProperties = {
  ...primaryButton,
  padding: "18px 28px",
  fontSize: 18,
};

const exitBtn: React.CSSProperties = {
  ...secondaryButton,
  marginTop: 8,
  padding: "14px 28px",
  fontSize: 14,
};

const exitConfirmPanel: React.CSSProperties = {
  border: `1px solid ${colors.border}`,
  background: "rgba(42, 31, 23, 0.72)",
  borderRadius: 8,
  padding: 28,
};

const exitConfirmTitle: React.CSSProperties = {
  ...typography.display,
  color: colors.washi,
  fontSize: 26,
};

const exitConfirmCopy: React.CSSProperties = {
  color: colors.steel,
  fontSize: 13,
  lineHeight: 1.5,
  margin: "12px 0 24px",
};

const exitConfirmActions: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
};

const exitCancelBtn: React.CSSProperties = {
  ...primaryButton,
  padding: "14px 20px",
  fontSize: 14,
};

const exitConfirmBtn: React.CSSProperties = {
  ...secondaryButton,
  padding: "14px 20px",
  fontSize: 14,
};

const campaignPanel: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
};

const campaignHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 14,
};

const backBtn: React.CSSProperties = {
  ...secondaryButton,
  padding: "9px 14px",
  fontSize: 12,
};

const phasePath: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  gap: 10,
};

const phaseNode: React.CSSProperties = {
  ...secondaryButton,
  minHeight: 106,
  padding: "12px 8px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
};

const phaseNumeral: React.CSSProperties = {
  ...typography.display,
  fontSize: 24,
};

const phaseName: React.CSSProperties = {
  fontSize: 12,
};

const phaseMeta: React.CSSProperties = {
  fontSize: 10,
  opacity: 0.7,
  letterSpacing: "0.04em",
  textTransform: "none",
};

const phaseDetail: React.CSSProperties = {
  border: `1px solid ${colors.border}`,
  background: "rgba(42, 31, 23, 0.68)",
  borderRadius: 8,
  padding: 18,
  display: "grid",
  gridTemplateColumns: "1fr auto",
  gap: 16,
  alignItems: "center",
};

const phaseDetailName: React.CSSProperties = {
  ...typography.display,
  color: colors.washi,
  fontSize: 24,
};

const phaseDetailSub: React.CSSProperties = {
  color: colors.steel,
  opacity: 0.76,
  fontSize: 13,
  marginTop: 8,
};

const thresholdText: React.CSSProperties = {
  color: colors.amber,
  fontSize: 13,
  justifySelf: "end",
};

const campaignBtn: React.CSSProperties = {
  ...primaryButton,
  gridColumn: "1 / -1",
  padding: "16px 28px",
  fontSize: 17,
};

const reviewActions: React.CSSProperties = {
  gridColumn: "1 / -1",
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
};

const reviewBtn: React.CSSProperties = {
  ...secondaryButton,
  padding: "12px 18px",
  fontSize: 12,
};

const fullWidthReviewBtn: React.CSSProperties = {
  gridColumn: "1 / -1",
};

const wieldPanel: React.CSSProperties = {
  position: "absolute",
  left: "9vw",
  bottom: "10vh",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const sectionLabel: React.CSSProperties = {
  ...typography.ui,
  color: colors.steel,
  opacity: 0.72,
  fontSize: 12,
};

const wieldBtn: React.CSSProperties = {
  ...secondaryButton,
  minWidth: 118,
  padding: "11px 18px",
  fontSize: 13,
};

const hint: React.CSSProperties = {
  margin: 0,
  color: colors.washi,
  opacity: 0.58,
  fontSize: 12,
};

const kataBtn: React.CSSProperties = {
  ...secondaryButton,
  padding: "14px 22px",
  fontSize: 14,
};

const musicBtn: React.CSSProperties = {
  ...secondaryButton,
  position: "absolute",
  right: "9vw",
  // 58px cleared the old kataBtn-alone row (~44px tall); bottomRightRow is
  // taller now that dataPanel (button + caption, ~60px) sits in it, so this
  // needs more headroom too — same class of "one block doesn't know the
  // other's height" issue, on the one absolute-positioned button left here.
  bottom: "calc(10vh + 78px)",
  padding: "12px 18px",
  fontSize: 13,
};

const kataLockedBtn: React.CSSProperties = {
  ...kataBtn,
  ...disabledButton,
};

// Single positioning anchor for the whole Data+Katas cluster — flexbox
// aligns their bottoms and spaces them, so nothing here is a guessed vh/px
// offset between independently-anchored blocks (that's what went wrong
// three times in a row: title, then the panel above it, then EXIT).
const bottomRightRow: React.CSSProperties = {
  position: "absolute",
  right: "9vw",
  bottom: "10vh",
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-end",
  gap: 20,
};

const dataPanel: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: 6,
  maxWidth: 220,
};

const dataHint: React.CSSProperties = {
  margin: 0,
  color: colors.washi,
  opacity: 0.58,
  fontSize: 11,
  textAlign: "right",
};
